import {
  type PaginationState,
  type SortingState,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useCallback, useEffect, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { FailedTranscriptionItem } from '@eluve/api-contract';
import {
  Button,
  ColDefBuilder,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  Link,
  ServerDataTable,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { useDialog } from '@eluve/utility-hooks';

const getUserDisplayName = (row: FailedTranscriptionItem) =>
  [row.firstName, row.lastName].filter(Boolean).join(' ') ||
  row.email ||
  'Unknown';

const columns = new ColDefBuilder<FailedTranscriptionItem>()
  .defaultSortable('tenantName', 'Organization')
  .linkSortable(
    'appointmentName',
    (row) =>
      `/admin/organizations/${row.tenantId}/appointments/${row.appointmentId}/files`,
    'Appointment',
  )
  .colDef({
    id: 'user',
    header: 'User',
    accessorFn: getUserDisplayName,
    cell: ({ row }) => {
      const name = getUserDisplayName(row.original);
      if (row.original.userId) {
        return <Link to={`/admin/users/${row.original.userId}`}>{name}</Link>;
      }
      return <Text size="xs">{name}</Text>;
    },
    enableSorting: true,
  })
  .defaultSortable('fileName', 'File Name')
  .defaultSortable('transcriptType', 'Transcript Type')
  .dateSortable('createdAt', 'Created At')
  .dateSortable('updatedAt', 'Updated At')
  .colDef({
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => (
      <RescheduleAction
        tenantId={row.original.tenantId}
        appointmentId={row.original.appointmentId}
        fileId={row.original.fileId}
      />
    ),
  })
  .build();

export const FailedTranscriptionsTab: React.FC = () => {
  const api = useApiClient();
  const [data, setData] = useState<FailedTranscriptionItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fetchDurationMs, setFetchDurationMs] = useState<number | null>(null);
  const toast = useToast();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'updatedAt', desc: true },
  ]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const startTime = performance.now();
    const sort = sorting[0];
    const result = await api.adminOnCall.getFailedTranscriptions({
      query: {
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        sortBy: sort?.id,
        sortDirection: sort?.desc ? 'desc' : 'asc',
      },
    });
    const elapsed = Math.round(performance.now() - startTime);
    setFetchDurationMs(elapsed);
    if (result.status === 200) {
      setData(result.body.items);
      setTotalCount(result.body.totalCount);
    } else {
      toast.error('Failed to load failed transcriptions. Please try again.');
    }
    setLoading(false);
  }, [api, pagination.pageIndex, pagination.pageSize, sorting]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount: Math.ceil(totalCount / pagination.pageSize),
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
  });

  return (
    <Stack gap={2}>
      <Stack gap={2} direction="horizontal">
        <Stack
          className="ml-auto"
          fullWidth={false}
          gap={2}
          direction="horizontal"
        >
          <Text inline size="xs" color="tertiary">
            {loading
              ? 'Loading...'
              : `${totalCount} failed transcription${totalCount !== 1 ? 's' : ''}${fetchDurationMs !== null ? ` · ${fetchDurationMs >= 1000 ? `${(fetchDurationMs / 1000).toFixed(1)}s` : `${fetchDurationMs}ms`}` : ''}`}
          </Text>
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <Icon name="RefreshCw" size="auto" />
            Refresh
          </Button>
        </Stack>
      </Stack>
      <Stack className="rounded-md border">
        <ServerDataTable<FailedTranscriptionItem>
          table={table}
          isPending={loading}
          placeholder="No failed transcriptions found."
        />
      </Stack>
    </Stack>
  );
};

const RescheduleAction: React.FC<{
  tenantId: string;
  appointmentId: string;
  fileId: string;
}> = ({ tenantId, appointmentId, fileId }) => {
  const api = useApiClient();
  const toast = useToast();
  const rescheduleDialog = useDialog();
  const [processing, setProcessing] = useState(false);

  const onReschedule = async () => {
    setProcessing(true);

    const result = await api.adminAppointment.rescheduleTranscription({
      params: { tenantId, appointmentId, fileId },
      body: {},
    });

    if (result.status === 200) {
      toast.success('Transcription rescheduled successfully');
    } else {
      toast.error('Failed to reschedule transcription');
    }

    setProcessing(false);
    rescheduleDialog.closeDialog();
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => rescheduleDialog.openDialog()}
      >
        Reschedule
      </Button>
      <Dialog
        open={rescheduleDialog.isDialogOpen}
        onOpenChange={rescheduleDialog.setIsDialogOpen}
      >
        <DialogContent
          size="lg"
          showCloseButton={false}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
            <DialogDescription>
              This will schedule a new transcription attempt.
              <br />
              <br />
              Note: a tag will also be added, indicating human intervention.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={processing}
              onClick={rescheduleDialog.closeDialog}
            >
              Cancel
            </Button>
            <Button disabled={processing} onClick={onReschedule}>
              {processing ? 'Processing...' : 'Reschedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
